import {
  ExerciseAssetDto,
  ExerciseAssetStatus,
  PaginatedListOfExerciseAssetDto,
} from "@growth-machine-llc/stridist-api-client";
import { useOptimisticUpdateMutation } from "../../../hooks/useOptimisticUpdateMutation";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY } from "../ExercisesTableScreen";
import ExerciseAssetsService from "../../../services/ExerciseAssetsService";
import { EXERCISE_LIBRARY_KEY_AUTOCOMPLETE_QUERY_KEY } from "../ExerciseLibraryAutocomplete";
import { EXERCISE_LIBRARY_ASSET_LIST_QUERY_KEY } from "../ExerciseLibraryExercisesList";

export const SELECTED_EXERCISE = "selected-exercise";

export const INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE = [
  EXERCISE_LIBRARY_KEY_AUTOCOMPLETE_QUERY_KEY,
  SELECTED_EXERCISE,
  EXERCISE_LIBRARY_ASSET_LIST_QUERY_KEY,
];

export const onUnArchivedSuccess = (
  queryClient: QueryClient,
  isArchivedExercise,
) => {
  INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE.forEach((queryKey) => {
    queryClient.invalidateQueries({
      queryKey: [queryKey],
      exact: false,
    });
  });

  queryClient.invalidateQueries({
    queryKey: [
      COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY,
      {
        status: isArchivedExercise
          ? ExerciseAssetStatus.ACTIVE
          : ExerciseAssetStatus.ARCHIVED,
      },
    ],
  });
};

export const useForcedRestoreLibraryExerciseMutation = (
  statusToProcess: ExerciseAssetStatus,
  disableToastAlerts: boolean = false,
  forcedRestoreFromArchivedRoute: boolean = false,
  exerciseAsset?: ExerciseAssetDto,
) => {
  const status = statusToProcess;

  return useOptimisticUpdateMutation({
    queryKey: [COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY, { status }],
    disableToastAlerts: disableToastAlerts,
    invalidateQueryOptions: {
      enabled: true,
      refetchType: "none",
    },
    mutationFn: ExerciseAssetsService.restoreExerciseAsset,
    optimisticUpdater: {
      updateFn: (oldData: PaginatedListOfExerciseAssetDto, variables) => {
        if (forcedRestoreFromArchivedRoute && exerciseAsset) {
          const matchedPublicIndex = oldData.items.findIndex(
            (exercise) =>
              exercise.name.trim() === exerciseAsset.name.trim() &&
              !exercise?.coachId,
          );

          if (matchedPublicIndex !== -1) {
            const updatedItems = [...oldData.items];
            updatedItems[matchedPublicIndex] = ExerciseAssetDto.fromJS({
              ...exerciseAsset,
              archived: false,
            });

            return {
              ...oldData,
              items: updatedItems,
            } as PaginatedListOfExerciseAssetDto;
          } else {
            return {
              ...oldData,
              items: [
                {
                  ...exerciseAsset,
                },
                ...oldData.items.filter(
                  (exercise) => exercise.id !== exerciseAsset.id,
                ),
              ],
            } as PaginatedListOfExerciseAssetDto;
          }
        } else {
          return {
            ...oldData,
            items: oldData.items.filter(
              (exercise) => exercise.id !== variables.id,
            ),
          } as PaginatedListOfExerciseAssetDto;
        }
      },
    },
  });
};

const useArchiveRestoreLibraryExerciseMutation = (
  status: ExerciseAssetStatus,
  disableToastAlerts: boolean = false,
) => {
  const mutationFn =
    status === ExerciseAssetStatus.PUBLISHED
      ? ExerciseAssetsService.archiveExerciseAsset
      : ExerciseAssetsService.restoreExerciseAsset;

  const statusToInvalidate =
    status === ExerciseAssetStatus.PUBLISHED
      ? ExerciseAssetStatus.ARCHIVED
      : ExerciseAssetStatus.PUBLISHED;

  const queryClient = useQueryClient();

  return useOptimisticUpdateMutation({
    queryKey: [COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY, { status }],
    mutationFn: mutationFn,
    disableToastAlerts: disableToastAlerts,
    optimisticUpdater: {
      updateFn: (oldData: PaginatedListOfExerciseAssetDto, variables) => {
        return {
          ...oldData,
          items: oldData.items.filter(
            (exercise) => exercise.id !== variables.id,
          ),
        } as PaginatedListOfExerciseAssetDto;
      },
    },
    invalidateQueryOptions: {
      enabled: true,
      refetchType: "none",
    },
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY,
            {
              status: statusToInvalidate,
            },
          ],
        });

        INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE.forEach((queryKey) => {
          queryClient.invalidateQueries({
            queryKey: [queryKey],
            exact: false,
          });
        });
      },
    },
  });
};

export default useArchiveRestoreLibraryExerciseMutation;
